"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/app/lib/utils";

interface AdSenseProps {
  adSlot: string;
  adFormat?: string;
  fullWidthResponsive?: boolean;
  className?: string;
}

declare global {
  interface Window {
    adsbygoogle: unknown[];
  }
}

type AdState = "loading" | "filled" | "unfilled";

const ADSENSE_SCRIPT_SRC =
  "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-3816171199708188";
const ADSENSE_SCRIPT_SELECTOR = 'script[src*="adsbygoogle.js"]';
const UNFILLED_TIMEOUT_MS = 5000;

let scriptLoadPromise: Promise<void> | null = null;

function loadAdSenseScript(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  const existing = document.querySelector<HTMLScriptElement>(ADSENSE_SCRIPT_SELECTOR);
  if (existing?.dataset.loaded === "true") {
    return Promise.resolve();
  }

  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("AdSense script failed")), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.src = ADSENSE_SCRIPT_SRC;
    script.crossOrigin = "anonymous";
    script.onload = () => {
      script.dataset.loaded = "true";
      resolve();
    };
    script.onerror = () => reject(new Error("AdSense script failed"));
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

function pushAdUnit() {
  (window.adsbygoogle = window.adsbygoogle || []).push({});
}

export function AdSense({
  adSlot,
  adFormat = "auto",
  fullWidthResponsive = true,
  className = "",
}: AdSenseProps) {
  const insRef = useRef<HTMLModElement>(null);
  const pushed = useRef(false);
  const [adState, setAdState] = useState<AdState>("loading");

  useEffect(() => {
    const ins = insRef.current;
    if (!ins) return;

    const markUnfilled = () => setAdState("unfilled");

    const updateState = () => {
      const status = ins.getAttribute("data-ad-status");
      if (status === "filled") {
        setAdState("filled");
      } else if (status === "unfilled") {
        markUnfilled();
      }
    };

    const observer = new MutationObserver(updateState);
    observer.observe(ins, {
      attributes: true,
      attributeFilter: ["data-ad-status"],
    });

    const timeout = window.setTimeout(() => {
      updateState();
      if (ins.getAttribute("data-ad-status") !== "filled") {
        markUnfilled();
      }
    }, UNFILLED_TIMEOUT_MS);

    loadAdSenseScript()
      .then(() => {
        if (pushed.current) return;
        pushed.current = true;
        try {
          pushAdUnit();
        } catch (e) {
          console.error("AdSense error:", e);
          markUnfilled();
        }
      })
      .catch((e) => {
        console.error("AdSense script error:", e);
        markUnfilled();
      });

    return () => {
      observer.disconnect();
      window.clearTimeout(timeout);
    };
  }, []);

  if (adState === "unfilled") {
    return null;
  }

  return (
    <div
      className={cn(
        className,
        adState === "loading" && "!my-0 min-h-0 overflow-hidden",
      )}
    >
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{
          display: "block",
          ...(adState === "loading"
            ? { minHeight: 0, height: 0, overflow: "hidden" }
            : undefined),
        }}
        data-ad-client="ca-pub-3816171199708188"
        data-ad-slot={adSlot}
        data-ad-format={adFormat}
        data-full-width-responsive={fullWidthResponsive ? "true" : "false"}
      />
    </div>
  );
}
